const MAX_BATCH_SIZE = 34;
const FLUSH_INTERVAL_MS = 5000;
const MAX_TEXT_LENGTH = 233;

let installed = false;
let queue = [];
let flushTimer = null;
let context = {};

const sessionId = (() => {
  const key = 'innerweb_training_telemetry_session';
  try {
    const existing = window.sessionStorage.getItem(key);
    if (existing) return existing;
    const generated = `tr-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    window.sessionStorage.setItem(key, generated);
    return generated;
  } catch {
    return `tr-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
})();

function endpointUrl() {
  const apiUrl = process.env.REACT_APP_API_BASE_URL || '';
  try {
    const url = new URL(apiUrl, window.location.origin);
    url.pathname = url.pathname.replace(/\/webapi\/training\/?$/, '/webapi/ux/track');
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch {
    return '/webapi/ux/track';
  }
}

function deviceType() {
  const width = window.innerWidth || 0;
  if (width <= 767) return 'mobile';
  if (width <= 1199) return 'tablet';
  return 'desktop';
}

function safeText(value) {
  if (value === undefined || value === null) return null;
  return String(value).replace(/\s+/g, ' ').trim().slice(0, MAX_TEXT_LENGTH) || null;
}

/**
 * 🔴 `[sekret-w-telemetrii]` (kontrola MR !34). `t` to token DOSTEPU do panelu stanowiska
 * (`stationAccess.js`, `PARAM_TOKENU`). Bez niego na tej liscie KAZDY klik na kiosku i kazde
 * zadanie stanowiska zapisywalo zywy token jawnym tekstem w `/webapi/ux/track` — trwala kopia
 * poswiadczenia poza paskiem adresu, czytelna dla szerszego kregu niz superadmin, ktory ten
 * token rotuje. Dokladnie to, czego docblock `stationAccess.js` zabrania przy `localStorage`.
 *
 * Nazwa parametru NIE MOZE rozjechac sie z ta lista — pilnuje tego bramka w
 * `RegistrationStation.tokenLeak.test.js`, ktora porownuje `PARAM_TOKENU` z jej zawartoscia.
 */
const PARAMETRY_DO_ZAMAZANIA = ['code', 'token', 'password', 'pass', 't'];

function scrubUrl(rawUrl) {
  if (!rawUrl) return '';
  try {
    const url = new URL(rawUrl, window.location.origin);
    PARAMETRY_DO_ZAMAZANIA.forEach((key) => {
      if (url.searchParams.has(key)) url.searchParams.set(key, '[redacted]');
    });
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    const wzorzec = new RegExp(`([?&](${PARAMETRY_DO_ZAMAZANIA.join('|')})=)[^&]+`, 'gi');
    return String(rawUrl).replace(wzorzec, '$1[redacted]');
  }
}

export const PARAMETRY_ZAMAZYWANE_W_TELEMETRII = PARAMETRY_DO_ZAMAZANIA;

function summarizePayload(payload) {
  if (payload === undefined || payload === null) return null;
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch {
      return safeText(payload);
    }
  }
  if (typeof payload !== 'object') return safeText(payload);

  const result = {};
  Object.entries(payload).slice(0, 21).forEach(([key, value]) => {
    const lower = key.toLowerCase();
    if (lower.includes('password') || lower.includes('token') || lower === 'code') {
      result[key] = '[redacted]';
    } else if (Array.isArray(value)) {
      result[key] = { type: 'array', length: value.length };
    } else if (value && typeof value === 'object') {
      result[key] = { keys: Object.keys(value).slice(0, 21) };
    } else {
      result[key] = safeText(value);
    }
  });
  return result;
}

function elementLabel(element) {
  return safeText(
    element.getAttribute('aria-label')
    || element.getAttribute('title')
    || element.innerText
    || element.value
    || element.name
    || element.id
    || element.tagName
  );
}

function elementId(element) {
  if (element.dataset && element.dataset.trackId) return element.dataset.trackId;
  if (element.id) return `#${element.id}`;
  if (element.name) return `${element.tagName.toLowerCase()}[name="${element.name}"]`;
  const label = elementLabel(element);
  return `${element.tagName.toLowerCase()}${label ? `:${label}` : ''}`.slice(0, 255);
}

function fieldValue(element) {
  const type = (element.type || '').toLowerCase();
  if (['password', 'hidden'].includes(type)) return '[redacted]';
  if (type === 'checkbox' || type === 'radio') return element.checked ? 'checked' : 'unchecked';
  return safeText(element.value);
}

function currentPagePath() {
  return scrubUrl(`${window.location.pathname}${window.location.search}${window.location.hash}`);
}

export function setTrainingTelemetryContext(nextContext) {
  context = {
    ...context,
    ...Object.fromEntries(
      Object.entries(nextContext || {}).filter(([, value]) => value !== undefined && value !== null && value !== '')
    ),
  };
}

export function trackTrainingEvent(eventType, elementIdValue, elementLabelValue, metadata = {}) {
  queue.push({
    elementId: elementIdValue || eventType,
    elementLabel: elementLabelValue || null,
    eventType,
    pagePath: currentPagePath(),
    routeName: 'training-reactor',
    deviceType: deviceType(),
    viewportWidth: window.innerWidth || 0,
    viewportHeight: window.innerHeight || 0,
    clientTimestamp: String(Date.now()),
    source: 'training-reactor',
    sessionId,
    trainingId: context.trainingId ? Number(context.trainingId) : undefined,
    userUuid: context.userUuid || undefined,
    metadata: {
      ...context,
      ...metadata,
      href: currentPagePath(),
    },
  });

  if (queue.length >= MAX_BATCH_SIZE) {
    flushTrainingTelemetry();
  } else {
    scheduleFlush();
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    flushTrainingTelemetry();
  }, FLUSH_INTERVAL_MS);
}

export function flushTrainingTelemetry() {
  if (!queue.length) return;
  const events = queue.splice(0, MAX_BATCH_SIZE);
  const body = JSON.stringify({ events });
  const url = endpointUrl();

  try {
    if (navigator.sendBeacon) {
      const ok = navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      if (ok) return;
    }
  } catch {
    // Fall through to fetch.
  }

  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    credentials: 'include',
    keepalive: true,
  }).catch(() => {
    // Telemetry must never break the training flow.
  });
}

export function installTrainingTelemetry(axiosInstance) {
  if (installed) return;
  installed = true;

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element
      ? event.target.closest('button,a,input,select,textarea,[role="button"],[data-track-id]')
      : null;
    if (!target) return;
    trackTrainingEvent('click', elementId(target), elementLabel(target), {
      tag: target.tagName.toLowerCase(),
      type: target.getAttribute('type') || null,
      value: target.matches('input,textarea,select') ? fieldValue(target) : null,
    });
  }, true);

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement)) return;
    trackTrainingEvent('change', elementId(target), elementLabel(target), {
      tag: target.tagName.toLowerCase(),
      type: target.getAttribute('type') || null,
      name: target.getAttribute('name') || null,
      value: fieldValue(target),
    });
  }, true);

  document.addEventListener('submit', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    trackTrainingEvent('submit', elementId(target), elementLabel(target), {
      tag: target.tagName.toLowerCase(),
    });
  }, true);

  window.addEventListener('popstate', () => {
    trackTrainingEvent('navigation', 'browser-navigation', 'Browser navigation');
  });

  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushTrainingTelemetry();
  });
  window.addEventListener('beforeunload', flushTrainingTelemetry);

  if (axiosInstance && axiosInstance.interceptors) {
    axiosInstance.interceptors.request.use((config) => {
      config.metadata = config.metadata || {};
      config.metadata.telemetryStart = Date.now();
      config.metadata.telemetryId = `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      trackTrainingEvent('http_request', config.metadata.telemetryId, `${(config.method || 'get').toUpperCase()} ${scrubUrl(config.url || '')}`, {
        method: (config.method || 'get').toUpperCase(),
        url: scrubUrl(config.url || ''),
        request: summarizePayload(config.data),
      });
      return config;
    });

    axiosInstance.interceptors.response.use((response) => {
      const config = response.config || {};
      const started = config.metadata?.telemetryStart || Date.now();
      trackTrainingEvent('http_response', config.metadata?.telemetryId || 'http-response', `${response.status} ${scrubUrl(config.url || '')}`, {
        method: (config.method || 'get').toUpperCase(),
        url: scrubUrl(config.url || ''),
        status: response.status,
        durationMs: Date.now() - started,
        response: summarizePayload(response.data),
      });
      return response;
    }, (error) => {
      const config = error.config || {};
      const started = config.metadata?.telemetryStart || Date.now();
      trackTrainingEvent('http_error', config.metadata?.telemetryId || 'http-error', `${error.response?.status || 'ERR'} ${scrubUrl(config.url || '')}`, {
        method: (config.method || 'get').toUpperCase(),
        url: scrubUrl(config.url || ''),
        status: error.response?.status || null,
        durationMs: Date.now() - started,
        message: safeText(error.message),
        response: summarizePayload(error.response?.data),
      });
      return Promise.reject(error);
    });
  }

  trackTrainingEvent('session_start', 'training-session', 'Training session started', {
    userAgent: navigator.userAgent,
    language: navigator.language,
  });
}
